/* oxlint-disable react/jsx-props-no-spreading */
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItemIndicator,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  ReactNode,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import Icon from '~/components/Icon';
import { Simplify } from '~/utils/types';
import Field from './Field';
import {
  basePickerFieldStyles,
  basePickerLabelStyles,
  basePickerListboxStyles,
  basePickerOptionStyles,
} from './sharedFieldStyles';

const MultiSelectContent = forwardRef<
  ElementRef<typeof DropdownMenuContent>,
  ComponentPropsWithoutRef<typeof DropdownMenuContent>
>(({ className, children, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPortal>
    <DropdownMenuContent
      {...props}
      ref={ref}
      sideOffset={sideOffset}
      className={twMerge(basePickerListboxStyles, className)}
      data-component="corso-combobox-listbox"
    >
      <div className="p-1">{children}</div>
    </DropdownMenuContent>
  </DropdownMenuPortal>
));
MultiSelectContent.displayName = 'MultiSelectContent';

const MultiSelectItem = forwardRef<
  ElementRef<typeof DropdownMenuCheckboxItem>,
  Simplify<
    Omit<
      ComponentPropsWithoutRef<typeof DropdownMenuCheckboxItem> & {
        addon?: ReactNode;
        value: string;
        selected: boolean;
        onSelectChange: (select: boolean) => void;
      },
      'checked' | 'onCheckedChange'
    >
  >
>(
  (
    { className, children, addon, value, selected, onSelectChange, ...props },
    ref,
  ) => (
    <DropdownMenuCheckboxItem
      {...props}
      ref={ref}
      className={twMerge(basePickerOptionStyles, className)}
      onSelect={(e) => e.preventDefault()} // prevent closing the menu on select of an item
      checked={selected}
      onCheckedChange={onSelectChange}
      data-component="corso-combobox-option"
    >
      {addon}
      <span>{children}</span>

      <DropdownMenuItemIndicator>
        <Icon icon="Check" className="text-on-surface-variant" />
      </DropdownMenuItemIndicator>
    </DropdownMenuCheckboxItem>
  ),
);
MultiSelectItem.displayName = 'MultiSelectItem';

const MultiSelectTrigger = forwardRef<
  ElementRef<typeof DropdownMenuTrigger>,
  ComponentPropsWithoutRef<typeof DropdownMenuTrigger>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuTrigger
    {...props}
    ref={ref}
    className={twMerge(basePickerFieldStyles, className)}
    data-component="corso-combobox"
  >
    {children}
    <Icon
      icon="ChevronDown"
      className="absolute top-4 right-4 text-on-surface-variant transition-transform group-data-invalid:text-critical group-data-[state=open]:-rotate-180"
    />
  </DropdownMenuTrigger>
));
MultiSelectTrigger.displayName = 'MultiSelectTrigger';

// only valueMissing is applicable for select
type ValidationConstraint = Extract<keyof ValidityState, 'valueMissing'>;

type MultiSelectProps = {
  id: string;
  name: string;
  label: string;
  description?: ReactNode;
  validationMessages?: Record<ValidationConstraint, string>;
  required?: boolean;

  options: {
    label: ReactNode;
    /** MUST be unique across all options. */
    value: string;
  }[];
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;

  className?: string;
};

/**
 * Implementation inspired by: https://github.com/shadcn-ui/ui/discussions/859#discussioncomment-10788172
 * TODO: migrate to BaseUI select and deprecate this component in favor of a more fully featured select that supports multi-select use cases out of the box
 */
function MultiSelect({
  id,
  name,
  label,
  description,
  validationMessages,
  required,

  options,
  value,
  defaultValue,
  onValueChange,

  className,
}: MultiSelectProps) {
  // need to control the select value to float the label
  const [fieldValue, setFieldValue] = useState<string[]>(
    value ?? defaultValue ?? [],
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);

  return (
    <Field.Root className={twMerge(className, 'relative')}>
      <DropdownMenu>
        <MultiSelectTrigger
          aria-required={required}
          aria-describedby={`${id}-error-text ${id}-help-text`}
          id={id}
          data-invalid={!isValid ? '' : undefined}
          data-empty={!fieldValue.length ? '' : undefined}
          // ? someday we may need a `disabled` state that would also need to be a peer to the `HelpText` like other form controls
        >
          {!!fieldValue.length && fieldValue.join(', ')}

          <Field.Label htmlFor={id} className={basePickerLabelStyles}>
            {label}
          </Field.Label>
        </MultiSelectTrigger>
        <MultiSelectContent data-component="corso-combobox-listbox">
          {options.map((option) => (
            <MultiSelectItem
              key={option.value}
              value={option.value}
              selected={fieldValue.includes(option.value)}
              onSelectChange={(selected) => {
                const updated = selected
                  ? [...fieldValue, option.value]
                  : fieldValue.filter((item) => item !== option.value);
                setFieldValue(updated);
                setIsValid(true);
                setErrorMessage(null);
                onValueChange?.(updated);
              }}
            >
              {option.label}
            </MultiSelectItem>
          ))}
        </MultiSelectContent>
      </DropdownMenu>
      <input
        aria-hidden
        tabIndex={-1}
        required={required}
        name={name}
        value={fieldValue}
        onChange={() => {}} // no-op hidden input that's effectively readonly, but must still participate in validation
        onInvalid={() => {
          setIsValid(false);
          setErrorMessage(
            validationMessages?.valueMissing ??
              'Please select at least one item from the list.',
          );
        }}
        className="sr-only"
      />
      {errorMessage && (
        <Field.ErrorText id={`${id}-error-text`}>
          {errorMessage}
        </Field.ErrorText>
      )}
      {description && !errorMessage && (
        <Field.HelpText id={`${id}-help-text`}>{description}</Field.HelpText>
      )}
    </Field.Root>
  );
}

export { MultiSelect };
